import { Card, Table, Tag, Button } from '@bancobarigui/barihaus';
import { message, Popconfirm, Tooltip, Dropdown, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { PlayCircleOutlined, StopOutlined, CloseCircleOutlined, CalendarOutlined, LinkOutlined, CopyOutlined, UserAddOutlined, VideoCameraOutlined, HistoryOutlined, MoreOutlined, ForwardOutlined } from '@ant-design/icons';
import { VideoSession, VideoSessionSchedule, videoSessionService } from '@/services/videoSession.service';
import { InspectionItem } from '@/services/types/inspection.types';
import { VideoSessionStatus, VideoSessionStatusColors } from '@/shared/enums/VideoSessionStatus';
import dayjs from 'dayjs';
import { useState } from 'react';
import { ManageParticipantsModal } from './ManageParticipantsModal';
import { VideoRoom } from './VideoRoom';
import { VideoSessionHistoryModal } from './VideoSessionHistoryModal';

interface VideoSessionListProps {
  videoSessions: VideoSession[];
  schedules?: VideoSessionSchedule[];
  inspectionItems?: InspectionItem[];
  onRefresh: () => void;
  onUpdate?: () => void;
}

export function VideoSessionList({
  videoSessions,
  onRefresh,
  onUpdate,
}: VideoSessionListProps) {
  const [generatingLinks, setGeneratingLinks] = useState<Set<string>>(new Set());
  const [selectedSession, setSelectedSession] = useState<VideoSession | null>(null);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [isVideoRoomOpen, setIsVideoRoomOpen] = useState(false);
  const [videoRoomSession, setVideoRoomSession] = useState<VideoSession | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historySession, setHistorySession] = useState<VideoSession | null>(null);
  const [cancelConfirmId, setCancelConfirmId] = useState<string | null>(null);
  const [failedToStartSessions, setFailedToStartSessions] = useState<Set<string>>(new Set());

  const getActionMenuItems = (record: VideoSession): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [];

    const isFinished = record.status === VideoSessionStatus.Completed ||
      record.status === VideoSessionStatus.Cancelled ||
      record.status === VideoSessionStatus.Expired;

    if (!isFinished && (record.status === VideoSessionStatus.InProgress || record.uniqueLink)) {
      items.push({
        key: 'join',
        icon: <VideoCameraOutlined />,
        label: 'Entrar na Chamada',
        onClick: () => handleJoinVideoRoom(record),
      });
    }

    // Iniciar - para sessões agendadas ou confirmadas
    if (record.status === VideoSessionStatus.Scheduled || record.status === VideoSessionStatus.Confirmed) {
      items.push({
        key: 'start',
        icon: <PlayCircleOutlined />,
        label: 'Iniciar Sessão',
        onClick: () => handleStartSession(record.id),
      });

      if (failedToStartSessions.has(record.id)) {
        items.push({
          key: 'skip',
          icon: <ForwardOutlined />,
          label: 'Finalizar sem vídeo',
          onClick: () => handleSkipSession(record.id),
        });
      }
    }

    // Finalizar - para sessões em progresso
    if (record.status === VideoSessionStatus.InProgress) {
      items.push({
        key: 'end',
        icon: <StopOutlined />,
        label: 'Finalizar Sessão',
        onClick: () => handleEndSession(record.id),
      });
    }

    // Participantes - apenas para sessões não finalizadas
    if (!isFinished) {
      items.push({
        key: 'participants',
        icon: <UserAddOutlined />,
        label: 'Gerenciar Participantes',
        onClick: () => {
          setSelectedSession(record);
          setIsParticipantsModalOpen(true);
        },
      });
    }

    // Histórico - para sessões completadas
    if (record.status === VideoSessionStatus.Completed) {
      items.push({
        key: 'history',
        icon: <HistoryOutlined />,
        label: 'Ver Histórico',
        onClick: () => {
          setHistorySession(record);
          setIsHistoryModalOpen(true);
        },
      });
    }

    // Cancelar - para sessões agendadas ou confirmadas
    if (record.status === VideoSessionStatus.Scheduled || record.status === VideoSessionStatus.Confirmed) {
      items.push({
        type: 'divider',
      });
      items.push({
        key: 'cancel',
        icon: <CloseCircleOutlined />,
        label: 'Cancelar Sessão',
        danger: true,
        onClick: () => setCancelConfirmId(record.id),
      });
    }

    return items;
  };

  const getStatusColor = (status: VideoSessionStatus) => {
    // Mapeamento de cores conforme DS
    if (status === VideoSessionStatus.Completed) return 'green';
    if (status === VideoSessionStatus.InProgress) return 'green';
    if (status === VideoSessionStatus.Scheduled) return 'blue';
    if (status === VideoSessionStatus.Confirmed) return 'blue';
    if (status === VideoSessionStatus.Cancelled) return 'red';
    if (status === VideoSessionStatus.Expired) return 'orange';
    return VideoSessionStatusColors[status] || 'default';
  };

  const handleStartSession = async (sessionId: string) => {
    try {
      await videoSessionService.startVideoSession(sessionId);
      message.success('Sessão iniciada com sucesso!');
      onRefresh();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao iniciar sessão');
      setFailedToStartSessions(prev => new Set(prev).add(sessionId));
    }
  };

  const handleSkipSession = async (sessionId: string) => {
    try {
      await videoSessionService.skipVideoSession(sessionId);
      message.success('Sessão finalizada sem vídeo!');
      setFailedToStartSessions(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
      onRefresh();
      onUpdate?.();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao finalizar sessão');
    }
  };

  const handleEndSession = async (sessionId: string) => {
    try {
      await videoSessionService.endVideoSession(sessionId);
      message.success('Sessão finalizada com sucesso!');
      onRefresh();
      onUpdate?.();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao finalizar sessão');
    }
  };

  const handleCancelSession = async (sessionId: string) => {
    try {
      await videoSessionService.cancelVideoSession(sessionId);
      message.success('Sessão cancelada com sucesso!');
      onRefresh();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao cancelar sessão');
    }
  };

  const handleGenerateLink = async (sessionId: string) => {
    setGeneratingLinks(prev => new Set(prev).add(sessionId));
    try {
      const link = await videoSessionService.generateUniqueLink(sessionId);
      await navigator.clipboard.writeText(link);
      message.success('Link gerado e copiado para a área de transferência!');
      onRefresh();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao gerar link');
    } finally {
      setGeneratingLinks(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const handleCopyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      message.success('Link copiado para a área de transferência!');
    } catch (error) {
      message.error('Erro ao copiar link');
    }
  };

  const handleJoinVideoRoom = async (session: VideoSession) => {
    if (session.status === VideoSessionStatus.Completed) {
      message.warning('Esta chamada já foi finalizada e não pode mais ser acessada.');
      return;
    }
    if (session.status === VideoSessionStatus.Cancelled) {
      message.warning('Esta chamada foi cancelada.');
      return;
    }
    if (session.status === VideoSessionStatus.Expired) {
      message.warning('Esta chamada expirou e não está mais disponível.');
      return;
    }

    try {
      let sessionToJoin = session;

      if (session.status !== VideoSessionStatus.InProgress) {
        sessionToJoin = await videoSessionService.startVideoSession(session.id);
        message.success('Sessão iniciada! Entrando na chamada...');
      }

      setVideoRoomSession(sessionToJoin);
      setIsVideoRoomOpen(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Erro ao entrar na chamada');
      setFailedToStartSessions(prev => new Set(prev).add(session.id));
    }
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number, record: VideoSession) => (
        <Tag color={getStatusColor(status)}>{record.statusDescription}</Tag>
      ),
    },
    {
      title: 'Data/Hora Agendada',
      key: 'scheduled',
      width: 130,
      render: (_: any, record: VideoSession) => (
        <div>
          <div>{dayjs(record.scheduledStartTime).format('DD/MM/YYYY')}</div>
          <div>{dayjs(record.scheduledStartTime).format('HH:mm')}</div>
          <div className="text-xs text-gray-500">
            até {dayjs(record.scheduledEndTime).format('HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: 'Participantes',
      key: 'participants',
      width: 110,
      render: (_: any, record: VideoSession) => (
        <div>
          <div>{record.participants.length} participante(s)</div>
          {record.maxParticipants && <div className="text-xs text-gray-500">/ {record.maxParticipants} máximo</div>}
        </div>
      ),
    },
    {
      title: 'Link',
      key: 'link',
      width: 100,
      render: (_: any, record: VideoSession) => {
        const shareableLink = record.guestLink || record.uniqueLink;
        if (shareableLink && (record.status === VideoSessionStatus.Scheduled ||
          record.status === VideoSessionStatus.InProgress ||
          record.status === VideoSessionStatus.Confirmed)) {
          return (
            <Tooltip title="Clique para copiar">
              <Button
                type="link"
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(shareableLink)}
                size="small"
              >
                Copiar Link
              </Button>
            </Tooltip>
          );
        }
        const label = record.status === VideoSessionStatus.Cancelled
          ? 'Cancelada'
          : record.status === VideoSessionStatus.Expired
            ? 'Expirada'
            : 'Finalizada';
        return (
          <Typography.Text type="secondary">
            {label}
          </Typography.Text>
        );
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      align: 'right' as const,
      render: (_: any, record: VideoSession) => (
        <Popconfirm
          title="Cancelar sessão?"
          description="Tem certeza que deseja cancelar esta sessão?"
          open={cancelConfirmId === record.id}
          onConfirm={() => {
            handleCancelSession(record.id);
            setCancelConfirmId(null);
          }}
          onCancel={() => setCancelConfirmId(null)}
          okText="Sim"
          cancelText="Não"
        >
          <Dropdown
            menu={{ items: getActionMenuItems(record) || [] }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button type="link" icon={<MoreOutlined />} size="small" className="!p-0">
              Opções
            </Button>
          </Dropdown>
        </Popconfirm>
      ),
    },
  ];

  if (videoSessions.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <CalendarOutlined className="text-4xl text-gray-400 mb-4" />
          <p className="text-gray-500">Nenhuma sessão de vídeo encontrada</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card title="Sessões de Videoconferência">
        <Table
          dataSource={videoSessions}
          columns={columns as any}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {isParticipantsModalOpen && selectedSession && (
        <ManageParticipantsModal
          open={isParticipantsModalOpen}
          videoSession={selectedSession}
          onClose={() => {
            setIsParticipantsModalOpen(false);
            setSelectedSession(null);
          }}
        />
      )}

      {isVideoRoomOpen && videoRoomSession && (
        <VideoRoom
          open={isVideoRoomOpen}
          videoSession={videoRoomSession}
          onClose={() => {
            setIsVideoRoomOpen(false);
            setVideoRoomSession(null);
          }}
          onLeave={() => {
            setIsVideoRoomOpen(false);
            setVideoRoomSession(null);
            onRefresh();
          }}
        />
      )}

      {isHistoryModalOpen && historySession && (
        <VideoSessionHistoryModal
          open={isHistoryModalOpen}
          videoSession={historySession}
          onClose={() => {
            setIsHistoryModalOpen(false);
            setHistorySession(null);
          }}
        />
      )}
    </>
  );
}

